import classnames from 'classnames'
import * as React from 'react'
import { Link } from 'react-router-dom'

import { useElementMeasure } from '../../hooks/use-element-measure'
import { BoxRounding } from '../box'
import { Button, ButtonSizeType } from '../button'
import { DateRangeBaseOption, DateRangeSelect } from './daterange-select'
import { LabelWithDelayedSpinner } from './label-with-delayed-spinner'
import { TimeframeBaseOption, TimeframeSelect } from './timeframe-select'

interface Props<TimeFrame, DateRange> {
  chartIndex?: number
  compactViewMaxBreakpointPx?: number
  timeFrameGroups: Array<{ label: string; timeframes: TimeFrame[] }>
  dateRangeGroups: Array<{ label: string; dateRanges: DateRange[] }>
  selectedTimeframe: string
  selectedDateRange: string | null
  onTimeframeSelect: (value: TimeFrame) => void
  onDateRangeSelect: (value: DateRange) => void
  favoriteTimeframes: string[]
  onFavoriteTimeframeToggle: (value: TimeFrame) => void
  size?: ButtonSizeType
  loadingTimeframe?: string
  rounding?: keyof typeof BoxRounding
  isDateRangeAvailable?: boolean
  containerElement?: HTMLElement
  className?: string
  isFavoritesEnabled?: boolean
  isScrollIntoViewEnabled?: boolean
}

function getTimeframeChipElementId({ timeframe, chartIndex }: { timeframe: string; chartIndex: number }) {
  return `${chartIndex}-${timeframe}`
}

export function TimeframeBar<TimeFrame extends TimeframeBaseOption, DateRange extends DateRangeBaseOption>({
  chartIndex = 0,
  compactViewMaxBreakpointPx = 400,
  timeFrameGroups,
  dateRangeGroups,
  selectedTimeframe,
  selectedDateRange,
  favoriteTimeframes,
  loadingTimeframe,
  onTimeframeSelect,
  onDateRangeSelect,
  onFavoriteTimeframeToggle,
  size,
  rounding = 'regular',
  isDateRangeAvailable = true,
  containerElement,
  className,
  isFavoritesEnabled = true,
  isScrollIntoViewEnabled = true,
}: Props<TimeFrame, DateRange>) {
  const [scrollWrapperElement, setScrollWrapperElement] = React.useState<HTMLDivElement | null>(null)
  const [timeframeButtonsScrollLocation, setTimeframeButtonsScrollLocation] = React.useState({
    isOnLeftEdge: true,
    isOnRightEdge: true,
  })
  const { setElementRef, elementWidth } = useElementMeasure(containerElement)
  const flatTimeframes = React.useMemo(() => timeFrameGroups.flatMap(({ timeframes }) => timeframes), [timeFrameGroups])
  const isStockDetailTimeframeBar = rounding === 'none' && size === 'regular'

  const timeframesInExpandedPanel = React.useMemo(
    () => flatTimeframes.filter(({ value }) => value === selectedTimeframe || favoriteTimeframes.includes(value)),
    [flatTimeframes, selectedTimeframe, favoriteTimeframes]
  )

  const isCompactView = elementWidth < compactViewMaxBreakpointPx || timeframesInExpandedPanel.length <= 1
  const checkTimeframeWrapperScrollPosition = React.useCallback(() => {
    if (scrollWrapperElement) {
      const isOnLeftEdge = scrollWrapperElement.scrollLeft === 0
      const isOnRightEdge =
        scrollWrapperElement.offsetWidth + scrollWrapperElement.scrollLeft >= scrollWrapperElement.scrollWidth

      if (
        timeframeButtonsScrollLocation.isOnLeftEdge !== isOnLeftEdge ||
        timeframeButtonsScrollLocation.isOnRightEdge !== isOnRightEdge
      ) {
        setTimeframeButtonsScrollLocation({ isOnLeftEdge, isOnRightEdge })
      }
    }
  }, [timeframeButtonsScrollLocation, scrollWrapperElement])

  React.useEffect(() => {
    checkTimeframeWrapperScrollPosition()
  }, [checkTimeframeWrapperScrollPosition, scrollWrapperElement, elementWidth])

  React.useEffect(() => {
    if (!isCompactView && isScrollIntoViewEnabled) {
      const timeframeElement = document.getElementById(
        getTimeframeChipElementId({ chartIndex, timeframe: selectedTimeframe })
      )
      timeframeElement?.scrollIntoView({ inline: 'center', block: 'nearest' })
    }
  }, [isCompactView, chartIndex, selectedTimeframe, elementWidth, isScrollIntoViewEnabled])

  return (
    <div
      className={classnames(
        '-m-0.5 flex w-full items-center overflow-hidden p-0.5',
        {
          'space-x-2': isCompactView && !isStockDetailTimeframeBar,
          'space-x-1': !isCompactView && !isStockDetailTimeframeBar,
        },
        className
      )}
      ref={setElementRef}
    >
      {isFavoritesEnabled && (
        <div className="flex-none">
          <TimeframeSelect
            chartIndex={chartIndex}
            size={size}
            rounding={rounding}
            selectedTimeframe={selectedTimeframe}
            timeFrameGroups={timeFrameGroups}
            favoriteTimeframes={favoriteTimeframes}
            isCompactView={isCompactView}
            isLoading={!!loadingTimeframe && isCompactView}
            onFavoriteTimeframeToggle={onFavoriteTimeframeToggle}
            onTimeframeSelect={onTimeframeSelect}
          />
        </div>
      )}
      {!isCompactView && (
        <div className="relative -m-0.5 overflow-hidden p-0.5">
          <div
            className={classnames(
              'pointer-events-none absolute top-0 left-0 z-10 h-full w-2 bg-gradient-to-r from-white to-[#fff0] dark:from-gray-800 dark:to-[#22262f00]',
              { 'opacity-0': timeframeButtonsScrollLocation.isOnLeftEdge }
            )}
          />
          <div
            ref={setScrollWrapperElement}
            className="time-frames-scrollable -m-0.5 flex flex-1 space-x-px overflow-y-auto p-0.5"
            onScroll={checkTimeframeWrapperScrollPosition}
            onWheel={(event) => {
              /*
               * Vertical scrolling doesn't scroll horizontally by default (it's possible when holding shift)
               * When using touchpad we may get values in both delatX and deltaY, so if someone's scrolling horizontally we don't want to do anything
               * but if they are scrolling vertically (deltaY > deltaX) we want to scroll horizontally the value they would nativally scroll in vertical direction
               * */
              const absDx = Math.abs(event.deltaX)
              const absDy = Math.abs(event.deltaY)
              if (absDy > absDx) {
                event.currentTarget.scrollLeft += event.deltaY
              }
            }}
          >
            <div className={classnames('w-0.5 flex-none', { 'ml-px': isStockDetailTimeframeBar })} />
            {timeframesInExpandedPanel.map((timeframe) => (
              <Button
                as={timeframe.href ? Link : undefined}
                to={timeframe.href!}
                reloadDocument={timeframe.href ? !FinvizSettings.hasUserPremium : undefined}
                key={timeframe.value}
                id={getTimeframeChipElementId({ chartIndex, timeframe: timeframe.value })}
                className="relative focus-visible:z-10"
                data-testid={`chart-${chartIndex}-favorite-timeframe-${timeframe.value}`}
                theme="chipTransparent"
                size={size}
                active={timeframe.value === selectedTimeframe}
                onClick={() => onTimeframeSelect(timeframe)}
                rounding={rounding}
              >
                <LabelWithDelayedSpinner
                  label={timeframe.labelShort}
                  isLoading={loadingTimeframe === timeframe.value}
                />
              </Button>
            ))}
            <div className="w-0.5 flex-none" />
          </div>
          <div
            className={classnames(
              'pointer-events-none absolute top-0 right-0 z-10 h-full w-2 bg-gradient-to-r from-[#fff0] to-white dark:from-[#22262f00] dark:to-gray-800',
              { 'opacity-0': timeframeButtonsScrollLocation.isOnRightEdge }
            )}
          />
        </div>
      )}
      {isDateRangeAvailable && (
        <div className={classnames('flex-none', { 'ml-px': isStockDetailTimeframeBar })}>
          <DateRangeSelect
            chartIndex={chartIndex}
            size={size}
            dateRangeGroups={dateRangeGroups}
            selectedDateRange={selectedDateRange}
            onDateRangeSelect={onDateRangeSelect}
            rounding={rounding}
          />
        </div>
      )}
    </div>
  )
}
